import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type ChartViewMode = 'bar' | 'line-cumulative' | 'line-rolling';

/**
 * User's chart view mode preferences
 * Key structure: `${userId}:${metricKey}` -> view mode
 */
interface ChartViewModeState {
  // Owner's preferences (persisted to localStorage)
  ownerPreferences: Record<string, ChartViewMode>;
  
  // Temporary preferences for viewing other profiles (not persisted)
  temporaryPreferences: Record<string, ChartViewMode>;
  
  // Currently viewed profile owner ID
  currentProfileOwner: string | null;
  
  // Current user ID (logged in user)
  currentUserId: string | null;
  
  // Actions
  setCurrentProfile: (ownerId: string, viewerId: string | null) => void;
  getViewMode: (metricKey: string) => ChartViewMode | undefined;
  setViewMode: (metricKey: string, mode: ChartViewMode) => void;
  resetTemporaryPreferences: () => void;
}

export const useChartViewModeStore = create<ChartViewModeState>()(
  persist(
    (set, get) => ({
      ownerPreferences: {},
      temporaryPreferences: {},
      currentProfileOwner: null,
      currentUserId: null,

      setCurrentProfile: (ownerId: string, viewerId: string | null) => {
        const state = get();
        
        // Clear temporary preferences when switching profiles
        // Load owner's preferences as the base
        set({
          currentProfileOwner: ownerId,
          currentUserId: viewerId,
          temporaryPreferences: {},
        });

        console.log(`[ChartViewMode] Set profile owner: ${ownerId}, viewer: ${viewerId || 'none'}`);
      },

      getViewMode: (metricKey: string) => {
        const state = get();
        const { currentProfileOwner, currentUserId, ownerPreferences, temporaryPreferences } = state;
        
        if (!currentProfileOwner) return undefined;
        
        const isOwner = currentUserId === currentProfileOwner;
        const key = `${currentProfileOwner}:${metricKey}`;
        
        // If viewing own profile, use owner preferences
        if (isOwner) {
          return ownerPreferences[key];
        }
        
        // If viewing another profile, check temporary preferences first
        // Fall back to owner's preference
        return temporaryPreferences[key] || ownerPreferences[key];
      },

      setViewMode: (metricKey: string, mode: ChartViewMode) => {
        const state = get();
        const { currentProfileOwner, currentUserId } = state;
        
        if (!currentProfileOwner) {
          console.warn('[ChartViewMode] Cannot set view mode without profile owner');
          return;
        }
        
        const isOwner = currentUserId === currentProfileOwner;
        const key = `${currentProfileOwner}:${metricKey}`;
        
        if (isOwner) {
          // Owner: persist to localStorage
          set((state) => ({
            ownerPreferences: {
              ...state.ownerPreferences,
              [key]: mode,
            },
          }));
          console.log(`[ChartViewMode] Owner set ${metricKey} to ${mode} (persisted)`);
        } else {
          // Visitor: store temporarily (will be cleared on page load)
          set((state) => ({
            temporaryPreferences: {
              ...state.temporaryPreferences,
              [key]: mode,
            },
          }));
          console.log(`[ChartViewMode] Visitor set ${metricKey} to ${mode} (temporary)`);
        }
      },

      resetTemporaryPreferences: () => {
        set({ temporaryPreferences: {} });
        console.log('[ChartViewMode] Cleared temporary preferences');
      },
    }),
    {
      name: 'gitfit-chart-view-mode',
      // Only persist owner preferences
      partialize: (state) => ({
        ownerPreferences: state.ownerPreferences,
      }),
    }
  )
);
